import React, { useState, useEffect, useCallback } from 'react';
import { ReloadOutlined, MailOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {
  adminService,
  SesStatus,
  SesIdentity,
  NotificationConfig,
} from '../../services/admin.service';

/**
 * AdminNotificationsPage — Fase A: monitoreo + identidades de SES.
 *
 * Muestra el estado de la cuenta SES (sandbox/producción, cuota, envíos),
 * las identidades verificadas, y permite verificar nuevas identidades y
 * enviar un correo de prueba. No edita el remitente ni las plantillas: eso
 * llega en las fases B y C.
 */
const AdminNotificationsPage: React.FC = () => {
  const [status, setStatus] = useState<SesStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newIdentity, setNewIdentity] = useState('');
  const [addingIdentity, setAddingIdentity] = useState(false);

  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const [config, setConfig] = useState<NotificationConfig | null>(null);
  const [fromInput, setFromInput] = useState('');
  const [savingFrom, setSavingFrom] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statusRes, configRes] = await Promise.all([
        adminService.getNotificationStatus(),
        adminService.getNotificationConfig(),
      ]);
      if (statusRes.success && statusRes.status) {
        setStatus(statusRes.status);
      } else {
        setError(statusRes.error || 'No se pudo cargar el estado de SES');
      }
      if (configRes.success && configRes.config) {
        setConfig(configRes.config);
        setFromInput(configRes.config.fromAddress);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando el estado de SES');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSaveFrom = async () => {
    const value = fromInput.trim();
    if (!value) return;
    setSavingFrom(true);
    setError(null);
    setNotice(null);
    try {
      const res = await adminService.updateNotificationConfig(value);
      if (res.success && res.config) {
        setConfig(res.config);
        setFromInput(res.config.fromAddress);
        setNotice(
          res.config.warning
            ? `Remitente guardado. Aviso: ${res.config.warning}`
            : 'Remitente actualizado. Aplica en ~5 min (caché de las Lambdas).'
        );
        await fetchStatus();
      } else {
        setError(res.error || 'No se pudo guardar el remitente');
      }
    } finally {
      setSavingFrom(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAddIdentity = async () => {
    const identity = newIdentity.trim().toLowerCase();
    if (!identity) return;
    setAddingIdentity(true);
    setError(null);
    setNotice(null);
    try {
      const res = await adminService.createNotificationIdentity(identity);
      if (res.success) {
        setNotice(res.result?.message || `Verificación iniciada para ${identity}.`);
        setNewIdentity('');
        await fetchStatus();
      } else {
        setError(res.error || 'No se pudo iniciar la verificación');
      }
    } finally {
      setAddingIdentity(false);
    }
  };

  const handleSendTest = async () => {
    const to = testTo.trim().toLowerCase();
    if (!to) return;
    setSendingTest(true);
    setError(null);
    setNotice(null);
    try {
      const res = await adminService.sendTestNotification(to);
      if (res.success) {
        setNotice(`Correo de prueba enviado a ${to} (id: ${res.result?.messageId || 'n/a'}).`);
        setTestTo('');
      } else {
        setError(res.error || 'No se pudo enviar el correo de prueba');
      }
    } finally {
      setSendingTest(false);
    }
  };

  const inProduction = status?.productionAccessEnabled === true;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <MailOutlined /> Notificaciones
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Estado del envío de correos (Amazon SES) e identidades verificadas.
                </p>
              </div>
              <button
                onClick={fetchStatus}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ReloadOutlined className={`${isLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-200">Error: {error}</p>
              </div>
            )}
            {notice && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-200">{notice}</p>
              </div>
            )}
          </div>

          {/* Estado de la cuenta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Acceso de producción</p>
              <div className="mt-2 flex items-center gap-2">
                {inProduction ? (
                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                    <CheckCircleOutlined /> Producción
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-semibold">
                    <WarningOutlined /> Sandbox
                  </span>
                )}
              </div>
              {!inProduction && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  En sandbox solo llegan correos a destinatarios verificados.
                </p>
              )}
            </div>

            <div className="p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Enviados (24 h)</p>
              <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                {status?.quota?.sentLast24Hours ?? '—'}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {' '}/ {status?.quota?.max24HourSend ?? '—'}
                </span>
              </p>
            </div>

            <div className="p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Rate máximo</p>
              <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                {status?.quota?.maxSendRate ?? '—'}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400"> /seg</span>
              </p>
            </div>

            <div className="p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Remitente</p>
              <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white break-all">
                {status?.fromAddress ?? '—'}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Región: {status?.region ?? '—'}</p>
            </div>
          </div>

          {/* Métricas 7 días */}
          {status?.metrics?.totals && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Actividad (últimos {status.metrics.windowDays} días)
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {['Send', 'Delivery', 'Bounce', 'Complaint', 'Reject'].map((m) => (
                  <div key={m} className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{m}</p>
                    <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                      {status.metrics!.totals[m] ?? 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remitente (from) configurable */}
          <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Remitente</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Dirección "from" de todas las notificaciones. Acepta{' '}
                <code>correo@dominio.com</code> o <code>Nombre &lt;correo@dominio.com&gt;</code>.
                El dominio debe estar verificado en SES. Los cambios aplican en ~5 min.
              </p>
            </div>
            <div className="p-5">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Kubiy <notificaciones@kubiy.com>"
                  value={fromInput}
                  onChange={(e) => setFromInput(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSaveFrom}
                  disabled={savingFrom || !fromInput.trim() || fromInput.trim() === config?.fromAddress}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {savingFrom ? 'Guardando…' : 'Guardar remitente'}
                </button>
              </div>
              {config && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Origen: {config.source === 'table' ? 'configurado' : 'valor por defecto'}
                  {config.updatedAt ? ` · actualizado ${new Date(config.updatedAt).toLocaleString('es-MX')}` : ''}
                  {config.source !== 'table' ? ` · default: ${config.defaultFrom}` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Identidades */}
          <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Identidades verificadas</h2>
            </div>
            <div className="p-5">
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  placeholder="correo@dominio.com o dominio.com"
                  value={newIdentity}
                  onChange={(e) => setNewIdentity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddIdentity()}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddIdentity}
                  disabled={addingIdentity || !newIdentity.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {addingIdentity ? 'Verificando…' : 'Verificar identidad'}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4 font-medium">Identidad</th>
                      <th className="py-2 pr-4 font-medium">Tipo</th>
                      <th className="py-2 pr-4 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(status?.identities ?? []).map((id: SesIdentity) => (
                      <tr key={id.name} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="py-2 pr-4 text-gray-900 dark:text-white break-all">{id.name}</td>
                        <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">
                          {id.type === 'DOMAIN' ? 'Dominio' : 'Correo'}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              id.verificationStatus === 'SUCCESS'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : id.verificationStatus === 'FAILED'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            }`}
                          >
                            {id.verificationStatus === 'SUCCESS'
                              ? 'Verificada'
                              : id.verificationStatus === 'FAILED'
                              ? 'Falló'
                              : 'Pendiente'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {!isLoading && (status?.identities ?? []).length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 text-center text-gray-500 dark:text-gray-400">
                          Sin identidades registradas.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Correo de prueba */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Enviar correo de prueba</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Usa el remitente real ({status?.fromAddress ?? 'notificaciones@kubiy.com'}). En sandbox,
                el destinatario debe estar verificado.
              </p>
            </div>
            <div className="p-5 flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder="destinatario@ejemplo.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendTest()}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testTo.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {sendingTest ? 'Enviando…' : 'Enviar prueba'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationsPage;
